import { getVariant } from '@chessbut/engine';
import { type Leaderboard, POINTS, addDays } from '@chessbut/shared';
import { useEffect, useState } from 'react';
import { Countdown } from '../components/Countdown.tsx';
import { LeaderboardTable } from '../components/LeaderboardTable.tsx';
import { api } from '../lib/api.ts';
import { navigate, useLocation } from '../lib/router.tsx';
import { canOpenVariant, useSession } from '../lib/session.ts';
import { openAuth } from '../lib/ui.ts';

type Tab = 'today' | 'challenge' | 'rating';

export function LeaderboardPage() {
  const { search } = useLocation();
  const { campaign, me } = useSession();
  const tab = (['today', 'challenge', 'rating'].includes(search.get('tab') ?? '') ? search.get('tab') : 'today') as Tab;
  const pool = search.get('pool') ?? 'overall';
  const [yesterday, setYesterday] = useState(false);
  const [board, setBoard] = useState<Leaderboard | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const today = campaign?.today;
  const day = today ? (yesterday ? addDays(today, -1) : today) : '';
  useEffect(() => {
    if (!campaign) return;
    setBoard(null);
    const q =
      tab === 'rating' ? `kind=rating&key=${encodeURIComponent(pool)}` : `kind=${tab === 'today' ? 'daily' : 'challenge'}&key=${day}`;
    api<Leaderboard>(`/api/leaderboard?${q}`)
      .then(setBoard)
      .catch(() => {});
  }, [tab, pool, day, campaign?.today, me?.id]);

  const setTab = (t: Tab) => navigate(`/ranks?tab=${t}${t === 'rating' ? `&pool=${pool}` : ''}`, { replace: true });
  const unit = tab === 'today' ? 'pts' : tab === 'challenge' ? 'moves' : '';

  return (
    <div className="page">
      <div className="spread">
        <h1 className="page-title">Ranks</h1>
        {campaign && tab !== 'rating' && !yesterday && (
          <span className="clock-note">
            resets in
            <span className="clock-face">
              <Countdown to={campaign.nextDayAt} />
            </span>
          </span>
        )}
      </div>
      <div className="tabs underline" role="tablist">
        <button role="tab" aria-selected={tab === 'today'} onClick={() => setTab('today')}>
          Today
        </button>
        <button role="tab" aria-selected={tab === 'challenge'} onClick={() => setTab('challenge')}>
          Challenge
        </button>
        <button role="tab" aria-selected={tab === 'rating'} onClick={() => setTab('rating')}>
          Ratings
        </button>
      </div>

      {tab !== 'rating' && campaign && (
        <div className="scroller">
          <button className={`chip ${!yesterday ? 'is-selected' : ''}`} onClick={() => setYesterday(false)}>
            Today
          </button>
          <button className={`chip ${yesterday ? 'is-selected' : ''}`} onClick={() => setYesterday(true)}>
            Yesterday
          </button>
        </div>
      )}
      {tab === 'rating' && campaign && (
        <div className="scroller">
          {['overall', ...campaign.released.filter((r) => canOpenVariant({ campaign, me }, r.variant)).map((r) => r.variant)].map((p) => {
            const v = getVariant(p);
            return (
              <button
                key={p}
                className={`chip ${pool === p ? 'is-selected' : v ? 'is-variant' : ''}`}
                style={v ? { ['--variant' as string]: v.info.accent } : undefined}
                onClick={() => navigate(`/ranks?tab=rating&pool=${p}`, { replace: true })}
              >
                {p === 'overall' ? 'Overall' : (v?.info.name ?? p)}
              </button>
            );
          })}
        </div>
      )}

      <section>
        {board ? (
          <LeaderboardTable
            rows={board.rows}
            me={board.me}
            unit={unit}
            meId={me?.id ?? null}
            empty={
              tab === 'today'
                ? 'Nobody has scored yet. Win a rated game and you’re on top.'
                : tab === 'challenge'
                  ? 'Nobody has beaten the daily bot yet.'
                  : `Nobody qualifies yet: ${pool === 'overall' ? 10 : 5} rated games gets you here.`
            }
          />
        ) : (
          <div className="center">
            <span className="spinner big" />
          </div>
        )}
        {me?.guest && (
          <button className="btn primary block top-gap" onClick={() => openAuth('signup', 'Leaderboards show players with accounts. Your guest progress comes with you.')}>
            Create an account to appear here
          </button>
        )}
      </section>

      <button className="text-link how-link" onClick={() => setRulesOpen((o) => !o)}>
        {rulesOpen ? 'Hide' : 'How'} points and ratings work
      </button>
      {rulesOpen && (
        <section className="card prose">
          <h3>Daily points</h3>
          <ul>
            <li>
              Rated online win: <b>{POINTS.win}</b> (+1 per {POINTS.upsetStep} rating points your opponent had on you, up to +{POINTS.upsetMax})
            </li>
            <li>
              Draw: <b>{POINTS.draw}</b> · Loss: <b>{POINTS.loss}</b> (if the game lasted 10+ moves)
            </li>
            <li>
              Everything on today’s variant counts <b>×{POINTS.featuredMultiplier}</b>
            </li>
            <li>
              Daily challenge (first try): win <b>{POINTS.challenge.win}</b>, draw <b>{POINTS.challenge.draw}</b>, loss <b>{POINTS.challenge.loss}</b>
            </li>
            <li>Only your first {POINTS.sameOpponentCap} games against the same opponent each day score.</li>
            <li>A win by resignation, timeout or abandonment scores once the game reaches 5 moves each.</li>
            <li>Resets at midnight New York time.</li>
          </ul>
          <h3>Ratings</h3>
          <p>
            Glicko-2, on the same scale as lichess: everyone starts at 1500. You get a rating per variant plus an overall ChessBut rating that every
            rated game moves. A <b>?</b> means it’s still provisional.
          </p>
        </section>
      )}
    </div>
  );
}
